import React, { useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, FormControlLabel, Checkbox } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Tooltip, Legend);

const allLabels = ['Retail', 'Automotriz', 'Manufactura', 'Alimentos', 'Salud/Farmacéutica', 'Construcción', 'Servicios logísticos'];

const warehouseUtilizationData2023 = {
  'Retail': 91,
  'Automotriz': 91,
  'Manufactura': 90,
  'Alimentos': 89,
  'Salud/Farmacéutica': 81,
  'Construcción': 81,
  'Servicios logísticos': 71,
};

const warehouseUtilizationData2024 = {
  'Retail': 86,
  'Automotriz': 93,
  'Manufactura': 88,
  'Alimentos': 89,
  'Salud/Farmacéutica': 70,
  'Construcción': 93,
  'Servicios logísticos': 91,
};

const colorMap = {
  'Retail': '#1F3D58',
  'Automotriz': '#476A8E',
  'Manufactura': '#4B73B1',
  'Alimentos': '#6D6D6D',
  'Salud/Farmacéutica': '#A2AEBB',
  'Construcción': '#B0B0B0',
  'Logística': '#172B3E',
};

const UtilizacionAlmacenRadar = () => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState(allLabels);

  const labels = selectedIndustries.length > 0 ? selectedIndustries : allLabels;

  const dataValues2023 = labels.map((label) => warehouseUtilizationData2023[label]);
  const dataValues2024 = labels.map((label) => warehouseUtilizationData2024[label]);
  const backgroundColors = labels.map((label) => colorMap[label]);

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Utilización del Almacén (2022)',
        data: dataValues2023,
        borderColor: 'rgba(255, 0, 0, 1)', // Color de las líneas
        borderWidth: 2,
        pointBackgroundColor: backgroundColors,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: backgroundColors,
        backgroundColor: 'rgba(0, 0, 0, 0)', // Sin relleno
      },
      {
        label: 'Utilización del Almacén (2023)',
        data: dataValues2024,
        borderColor: 'rgba(71, 106, 142, 1)', // Color de las líneas
        borderWidth: 2,
        pointBackgroundColor: backgroundColors,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: backgroundColors,
        backgroundColor: 'rgba(0, 0, 0, 0)', // Sin relleno
      },
    ],
  };

 const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.raw}%`,
      },
    },
    datalabels: {
      color: '#000', // Cambia el color de las etiquetas
      font: {
        size: 14, // Ajusta el tamaño de las etiquetas
        weight: 'bold',
      },
      formatter: (value) => `${value}%`, // Formato del valor
      anchor: (context) => {
        // Alterna la posición ancla entre datasets
        return context.datasetIndex === 0 ? 'end' : 'center';
      },
      align: (context) => {
        // Alterna la alineación para cada dataset
        return context.datasetIndex === 0 ? 'start' : 'end';
      },
      offset: (context) => {
        // Aumenta la separación para evitar amontonamientos
        return context.datasetIndex === 0 ? 10 : 20;
      },
    },
  },
  scales: {
    r: {
      beginAtZero: true,
      max: 100,
      ticks: {
        display: false, // Oculta los números de las divisiones radiales

        stepSize: 20, // Espaciado más amplio
        showLabelBackdrop: false, // Elimina el fondo de las etiquetas
        font: {
          size: 10,
        },
        color: '#1F3D58',
      },
      angleLines: { color: '#e0e0e0' },
      grid: { color: '#e0e0e0' },
      pointLabels: {
        font: {
          size: 13,
        },
        color: '#1F3D58',
        padding: 50, // Agrega espacio adicional a las etiquetas radiales
      },
    },
  },
};
  const handleCheckboxChange = (industry) => {
    const updatedIndustries = selectedIndustries.includes(industry)
      ? selectedIndustries.filter((item) => item !== industry)
      : [...selectedIndustries, industry];
    setSelectedIndustries(updatedIndustries);
  };

  const infoContent = {
    Analisis: 'En 2023, la utilización de almacén muestra una mejora notable en sectores como Construcción, Automotriz y Servicios logísticos, alcanzando niveles del 93% y 91%, respectivamente. Estos resultados reflejan una mejor planificación del espacio y mayor eficiencia en la operación logística, optimizando los recursos de almacenamiento en distintos sectores productivos.',
    formula: 'Utilización del Almacén (%) = (Espacio utilizado / Espacio total disponible) × 100',
    description: 'Esta gráfica de radar muestra la utilización del almacén para diferentes sectores industriales en los años 2022 y 2023. Este indicador evalúa qué porcentaje del espacio total disponible en el almacén está siendo utilizado.',
  };
  

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Box display="flex" justifyContent="center" flexWrap="wrap" mb={2}>
        {allLabels.map((industry) => (
          <FormControlLabel
            key={industry}
            control={
              <Checkbox
                checked={selectedIndustries.includes(industry)}
                onChange={() => handleCheckboxChange(industry)}
                color="primary"
              />
            }
            label={industry}
          />
        ))}
      </Box>
      <div style={{ width: '100%', height: '357px' }}>
        <Radar data={chartData} options={options} />
      </div>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
        <p><strong>Análisis:</strong> {infoContent.Analisis}</p>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
          
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
  
};

export default UtilizacionAlmacenRadar;
